import './previewPinVerifyPage.scss';

import { ThemeProvider } from '@mui/material/styles';
import { toPhoneString } from '@thesavingsgroup/utils/toPhoneString';
import isEmpty from 'lodash/isEmpty';
import uniqueId from 'lodash/uniqueId';
import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import vehicleSUVSideView from '../../../assets/vehicle-suv-sideview-two.png';
import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { CheckCircleIcon } from '../../../components/Icons/CheckCircleIcon';
import { ErrorOutlineIcon } from '../../../components/Icons/ErrorOutlineIcon';
import { OTP } from '../../../components/OTP/OTP';
import { Toast } from '../../../components/Toast/Toast';
import { resetStore } from '../../../store/_core/resetStore';
import useApiStore from '../../../store/apiStore/useApiStore';
import { HydrateSource } from '../../../store/applyStore/enums/HydrateSource';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import useParameterStore from '../../../store/parameterStore/useParameterStore';
import { DeliverPinLocation } from '../../../store/pinStore/enums/DeliverPinLocation';
import { useCountdownTimer } from '../../../store/pinStore/hooks/useCountdownTimer';
import usePinStore from '../../../store/pinStore/usePinStore';
import usePreviewStore from '../../../store/previewStore/usePreviewStore';
import { useNavigateWithCallback } from '../../../store/routeStore/hooks/useNavigateWithCallback';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useUserStore from '../../../store/userStore/useUserStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();
  const navigateWithCallback = useNavigateWithCallback();

  const { t } = useTranslation();
  const { isLoading } = useApiStore();
  const { applicationType, leadChannelCode, vehicle_vin } = useParameterStore();
  const { hydrate } = useApplyStore();
  const {
    clearPin,
    deliverPin,
    deliverPinFailedMessage,
    deliverPinRetrySuccess,
    pin,
    setPin,
    computed: { hasValidPin },
  } = usePinStore();
  const { firstName, lastName, primaryPhoneNumber, primaryEmailAddress } =
    usePreviewStore();
  const {
    clearPinPurpose,
    clearPinRetryState,
    setUserFirstName,
    setUserLastName,
    setUserSignedUp,
    userAuthenticate,
    userCreate,
    userUpdate,
    userCreateFailedMessage,
    userAuthenticateFailedMessage,
    computed: { shouldCreateUser, isPinPurposeEmpty },
    pinPurpose,
    getDeliverPinParams,
  } = useUserStore();

  const resendTimerDuration = 15; // in seconds
  const [seconds, setSecondsLeft] = useState(0);
  const { startTimer, stopTimer } = useCountdownTimer(
    setSecondsLeft,
    resendTimerDuration,
  );

  const buttonText = useMemo((): string => {
    if (seconds > 0) {
      return t('PreviewPinVerifyPage.resendCodeDelay', {
        delay: seconds,
      });
    }
    return t('PreviewPinVerifyPage.resendCode');
  }, [t, seconds]);

  const formSubmit = async (event?: FormEvent): Promise<void> => {
    event?.preventDefault();
    if (!pin) {
      return;
    }
    // Set before creating/authenticating so SessionGuard does not briefly redirect
    // the still-authenticating user away from this non-authenticated route.
    setUserSignedUp(true);
    if (shouldCreateUser) {
      await userCreate(
        firstName,
        lastName,
        primaryEmailAddress,
        primaryPhoneNumber,
        pin,
      );
    } else {
      await userAuthenticate(
        primaryPhoneNumber,
        pin,
        leadChannelCode,
        pinPurpose,
      );

      // only update the profile once signed in: without a session the PATCH
      // is a 401, which redirects away from the error just shown
      if (useUserStore.getState().userAuthenticateSuccess) {
        setUserFirstName(firstName);
        setUserLastName(lastName);
        await userUpdate();
      }
    }
    const { userCreateSuccess, userAuthenticateSuccess } =
      useUserStore.getState();
    if (userCreateSuccess || userAuthenticateSuccess) {
      const applyRoute = await hydrate({
        applicationType,
        source: HydrateSource.PREVIEW,
        vin: vehicle_vin,
      });
      navigateWithCallback(applyRoute, (): void => {
        resetStore(usePreviewStore);
        clearPinPurpose();
      });
    }
  };

  const resendCode = async (): Promise<void> => {
    if (primaryPhoneNumber) {
      startTimer();
      clearPinRetryState();
      const deliverPinParams = getDeliverPinParams(
        DeliverPinLocation.RETRY,
        primaryPhoneNumber,
        primaryEmailAddress,
      );
      await deliverPin(deliverPinParams);
      clearPin();
    }
  };

  useEffect((): (() => void) => {
    if (!primaryPhoneNumber || isPinPurposeEmpty) {
      navigate('/sign-up');
    }
    startTimer();
    return (): void => {
      stopTimer();
    };
  }, [primaryPhoneNumber]);

  useEffect((): void => {
    if (userCreateFailedMessage || userAuthenticateFailedMessage) {
      clearPin();
    }
  }, [userCreateFailedMessage, userAuthenticateFailedMessage]);

  useEffect((): (() => void) => {
    // Clearing pin on unmount so the next pin page starts fresh
    return (): void => {
      clearPin();
    };
  }, []);

  useEffect((): void => {
    // Auto-submit once the last digit is entered
    if (hasValidPin) {
      formSubmit();
    }
  }, [hasValidPin]);

  return (
    <ThemeProvider theme={theme}>
      <div className="page preview-pin-verify-page">
        <Toast
          open={deliverPinRetrySuccess}
          onClose={(): void => {
            usePinStore.setState({ deliverPinRetrySuccess: false });
          }}
        >
          <Alert
            data-testid={locator.pages.previewPinVerify.alert.success}
            icon={<CheckCircleIcon />}
            severity="success"
          >
            {t('PreviewPinVerifyPage.codeSent')}
          </Alert>
        </Toast>

        <Toast
          open={!isEmpty(deliverPinFailedMessage)}
          onClose={(): void => {
            usePinStore.setState({ deliverPinFailedMessage: null });
          }}
        >
          <Alert
            data-testid={locator.pages.previewPinVerify.alert.error}
            icon={<ErrorOutlineIcon />}
            severity="error"
          >
            {deliverPinFailedMessage}
          </Alert>
        </Toast>

        <Header showLogo splitLayout />

        <main>
          <figure>
            <img
              alt={t('imageAlts.vehicleSUVSideView')}
              src={vehicleSUVSideView}
            />
          </figure>
          <form noValidate onSubmit={formSubmit}>
            <div className="form-content">
              <h1 data-testid={locator.pages.previewPinVerify.form.h1}>
                {t('PreviewPinVerifyPage.title')}
              </h1>
              <h3 data-testid={locator.pages.previewPinVerify.form.h3}>
                <Trans
                  components={[<u key={uniqueId('span-')} />]}
                  i18nKey={'PreviewPinVerifyPage.subtitle'}
                  values={{
                    phoneNumber: toPhoneString(primaryPhoneNumber),
                  }}
                />
              </h3>

              <OTP
                disabled={isLoading}
                error={userCreateFailedMessage || userAuthenticateFailedMessage}
                errorTestId={locator.pages.previewPinVerify.form.pinInputError}
                inputTestId={locator.pages.previewPinVerify.form.pinInput}
                value={pin}
                onChange={setPin}
              />

              <Button
                data-testid={locator.pages.previewPinVerify.form.resendPinLink}
                disabled={seconds > 0}
                loader={isLoading}
                fullWidth
                onClick={resendCode}
              >
                {buttonText}
              </Button>
            </div>
            <footer data-testid={locator.pages.previewPinVerify.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>
    </ThemeProvider>
  );
}

Component.displayName = 'PreviewPinVerifyPage';
